import { memo, useCallback, useId } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { ChevronDown } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Flex } from "@similarweb/ui-components/foundations";

import { TruncatedTextWithTooltip } from "app/pages/sales-intelligence/common-components/TruncatedText";
import type { TeamListItem } from "app/pages/sales-intelligence/domain/teams/types/team-list-item";

import type { ImpactedAsset, ImpactedAssetGroup } from "../../types/impactReport";
import { industryTransitionKeys } from "../../utils/i18n";

import { IndustryMappingPreview } from "./IndustryMappingPreview";
import { ImpactedAssetRowMetadata } from "./ImpactedAssetRowMetadata";
import {
    AssetRowWrapper,
    CollapsibleContent,
    CollapsibleRegion,
    RowHeaderName,
    RowToggleButton,
    RowToggleIcon,
} from "./styles";

type ImpactedAssetRowProps = {
    readonly asset: ImpactedAsset;
    readonly group: ImpactedAssetGroup;
    readonly rowKey: string;
    readonly isExpanded: boolean;
    readonly onToggle: (group: ImpactedAssetGroup, rowKey: string) => void;
    readonly dataAutomation: string;
    readonly teamsById?: ReadonlyMap<number, TeamListItem>;
};

const ImpactedAssetRowComponent = ({
    asset,
    group,
    rowKey,
    isExpanded,
    onToggle,
    dataAutomation,
    teamsById,
}: ImpactedAssetRowProps) => {
    const { translate } = useTranslate();
    const contentId = useId();
    const titleId = useId();

    const handleToggle = useCallback(() => onToggle(group, rowKey), [group, onToggle, rowKey]);

    return (
        <AssetRowWrapper data-automation={dataAutomation}>
            <RowToggleButton
                type="button"
                aria-expanded={isExpanded}
                aria-controls={contentId}
                aria-label={translate(
                    isExpanded ? industryTransitionKeys.reportRowCollapse : industryTransitionKeys.reportRowExpand,
                    { name: asset.name },
                )}
                onClick={handleToggle}
                data-automation={`${dataAutomation}-toggle`}
            >
                <Flex
                    gap="8px"
                    alignItems="center"
                    flex="1"
                    minWidth="0"
                >
                    <RowToggleIcon $isExpanded={isExpanded}>
                        <ChevronDown
                            size="sm"
                            color={colorsPalettes.carbon[300]}
                        />
                    </RowToggleIcon>
                    <RowHeaderName>
                        <TruncatedTextWithTooltip
                            id={titleId}
                            fontVariant="subtitle2"
                            color={colorsPalettes.carbon[500]}
                        >
                            {asset.name}
                        </TruncatedTextWithTooltip>
                    </RowHeaderName>
                </Flex>

                <ImpactedAssetRowMetadata
                    asset={asset}
                    group={group}
                    teamsById={teamsById}
                />
            </RowToggleButton>

            {isExpanded ? (
                <CollapsibleRegion
                    id={contentId}
                    role="region"
                    aria-labelledby={titleId}
                >
                    <CollapsibleContent>
                        <IndustryMappingPreview asset={asset} />
                    </CollapsibleContent>
                </CollapsibleRegion>
            ) : null}
        </AssetRowWrapper>
    );
};

/**
 * Memoized on a boolean rather than the expansion set: a fresh `Set` identity per toggle would fail
 * the shallow compare for every row, so only the two rows whose state actually flipped re-render.
 */
export const ImpactedAssetRow = memo(ImpactedAssetRowComponent);
